import { useDeletePartsMutation } from "@common/api/parts"
import { formatCount } from "@common/lib/number"
import { handleError } from "@common/utils"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import z from "zod"
import { DeleteDialog } from "@/components/layout/list-table-menu"
import useCustomToast from "@/hooks/use-custom-toast"
import { requirePermission } from "@/lib/require-permission"

const searchSchema = z.object({
  ids: z.preprocess(
    (value) =>
      Array.isArray(value) ? value : value === undefined ? undefined : [value],
    z.array(z.string().uuid()).catch([]),
  ),
})

export const Route = createFileRoute("/_layout/inventory/delete/bulk")({
  validateSearch: (search) => searchSchema.parse(search),
  beforeLoad: async ({ context: { queryClient }, search }) => {
    await requirePermission(queryClient, "part:delete", "/")
    if (search.ids.length === 0) {
      throw redirect({ to: "/inventory", search: true })
    }
  },
  component: DeletePartsDialog,
})

function counted(count: number): string {
  return `${formatCount(count)} part${count === 1 ? "" : "s"}`
}

function DeletePartsDialog(): React.ReactElement {
  const { ids } = Route.useSearch()
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const remove = useDeletePartsMutation()

  const close = () =>
    navigate({
      from: Route.fullPath,
      to: "/inventory",
      search: ({ ids: _, ...previous }) => previous,
    })

  return (
    <DeleteDialog
      noun={ids.length === 1 ? "part" : counted(ids.length)}
      pending={remove.isPending}
      onCancel={close}
      onConfirm={() =>
        remove.mutate(
          { partBulkIds: { part_ids: ids } },
          {
            onSuccess: ({ deleted }) => {
              showToast(
                "Deleted",
                `${counted(deleted.length)} deleted.`,
                "success",
              )
              close()
            },
            onError: (error) => handleError(error, showToast),
          },
        )
      }
    />
  )
}
